'use client';
import { Button, Input, MinthrSelect, Sidebar } from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import React, { FC, useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  DeleteFile,
  GetCurrency,
  UpdateOrCreateExpense,
} from 'features/expenses/services/Expenses';
import ExpenseDetails from './ExpenseDetails';
import { getCollaborateurs } from 'services/filtersService';
import FileUpload from 'components/common/FileUpload';
import {
  NoteDetailResponse,
  NotesFormValues,
} from 'features/expenses/types/type';
import MinthrDateRangePicker from 'components/common/MinthrDateRangePicker';
import { useParams } from 'next/navigation';
import { useAuthContext } from 'contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { EnumProjectModule } from 'features/project/enums/enums';
import { getProjectsUser } from 'features/project/services/services';
import { useDialog } from 'contexts/DialogContext';
import MintErrorMessage from 'components/common/MintErrorMessage';

interface CreateExpenseFormProps {
  noteId?: number;
  toggleRefresh?: () => void;
  toggleRefreshDT?: () => void;
  statsRefetch?: () => void;
  model?: NoteDetailResponse;
  userId?: string;
}

const CreateExpenseForm: FC<SideBarProps<CreateExpenseFormProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { closeSidebar, openSidebar } = useSidebarManager();
  const [usersList, setUsersList] = useState<SelectItem[]>([]);
  const [projectsList, setProjectData] = useState<SelectItem[]>([]);
  const [currencyList, setCurrencyList] = useState<
    { id: string | null; title: string | null }[]
  >([]);
  const [errors, setErrors] = useState<string>('');
  const [isFileVisible, setIsFileVisible] = useState(false);
  const [filesNames, setFilesNames] = useState<FileType[]>(
    params?.model?.filesNames ?? [],
  );
  const { id: userId } = useParams();
  const { user } = useAuthContext();
  const userIdString = user.userId;
  const dialog = useDialog();

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t('RequiredAttribute')),
    currency: Yup.string().required(t('RequiredAttribute')),
  });

  const toggleFileVisibility = () => {
    setIsFileVisible((prevVisibility) => !prevVisibility);
  };

  const fetchCurrencies = async () => {
    const response = await GetCurrency();
    if (response.success) {
      const formattedOptions = response?.data.map((item: SelectItem) => ({
        id: item.value,
        title: item.text,
      }));
      setCurrencyList(formattedOptions);
    } else {
      toast.error(response.message);
    }
  };

  const fetchUsers = async () => {
    const response = await getCollaborateurs();
    if (response.success) {
      setUsersList(response.data);
    }
  };

  const fetchProjectData = async (userId: string) => {
    const response = await getProjectsUser({
      userId: userId,
      module: EnumProjectModule.Expense,
    });
    if (response.success) {
      setProjectData(response.data);
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchUsers();
    const targetUserId =
      params?.model?.affectedUserId ?? userId?.toString() ?? userIdString;
    if (targetUserId) {
      fetchProjectData(targetUserId);
    }
  }, []);

  const handleSubmit = async (
    values: NotesFormValues,
    { setSubmitting }: FormikHelpers<NotesFormValues>,
  ) => {
    const formData = new FormData();
    formData.append(
      'affectedUserId',
      (userId as string) || values.affectedUserId,
    );
    formData.append('title', values.title);
    formData.append('currency', values.currency);
    formData.append('executionDate', values.executionDate);
    formData.append('description', values.description);
    if (projectsList && projectsList.length > 0) {
      formData.append('projectId', values.projectId);
    }

    if (values.files) {
      values.files.forEach((file) => {
        formData.append('files', file);
      });
    }
    formData.append('id', values.id as string);
    try {
      const response = await UpdateOrCreateExpense(formData);
      if (response.success) {
        closeSidebar(CreateExpenseForm);
        params?.toggleRefreshDT?.();
        openSidebar(ExpenseDetails, true, {
          noteId: response.value as number,
          toggleRefreshDT: params?.toggleRefreshDT,
        });
        toast.success(response.message);
      } else {
        if (response.message !== '') setErrors(response.message);
        else setErrors(response.validationErrors[0].message);
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handelDeleteFile = async (id: number): Promise<void> => {
    const confirmed = await dialog.delete(
      'File.delete_title',
      'File.delete_description',
    );

    if (confirmed) {
      const response = await DeleteFile(id);
      if (response.success) {
        toast.success(t('Default.succesMessage'));
        setFilesNames((prev) => [...prev.filter((item) => id != item.id)]);
      } else {
        toast.error(response.message);
      }
    }
  };

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-5 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <img className="minthr-icon-m" src="/icons/wallet.svg" alt="" />
        </div>
        <h3>{t('Notes.de.frais')}</h3>
        <button
          onClick={() => closeSidebar(CreateExpenseForm)}
          className="minthr-sidebar-close"
          type="button"
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>

      <Formik
        initialValues={{
          affectedUserId:
            params?.model?.affectedUserId ??
            userId?.toString() ??
            userIdString ??
            '',
          title: params?.model?.title || '',
          currency: params?.model?.currency || '',
          executionDate: params?.model?.executionDate
            ? new Date(params?.model?.executionDate).toISOString()
            : new Date().toISOString(),
          description: params?.model?.description || '',
          files: params?.model?.files || [],
          filesNames: params?.model?.filesNames || [],
          id: params?.model?.id || '',
          projectId: params?.model?.projectId?.toString() || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="w-100">
            {errors && <MintErrorMessage message={errors} />}

            {!userId && !params?.userId && (
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <MinthrSelect
                    options={usersList.map((item) => ({
                      label: item.text,
                      value: item.value,
                    }))}
                    name="affectedUserId"
                    label={t('DocDemand.F.Users')}
                    value={values.affectedUserId}
                    onchange={(e: SelectOptionResponse) => {
                      setFieldValue('affectedUserId', e.value);
                      setFieldValue('projectId', '');
                      setProjectData([]);
                      fetchProjectData(e.value as string);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="row g-0 minthr-mb-5">
              <div className="col-sm-12 expenses-sidebar-step1">
                <Input
                  type="text"
                  name="title"
                  label={t('Note.title')}
                  className="minthr-full-input"
                />
              </div>
            </div>

            <div className="row g-0 minthr-mb-5">
              <div className="col-sm-12 expenses-sidebar-step2">
                <MinthrSelect
                  options={currencyList.map((item) => ({
                    label: item.title,
                    value: item.id,
                  }))}
                  name="currency"
                  label={t('devise')}
                />
              </div>
            </div>

            <div className="row g-0 minthr-mb-5">
              <div className="col-sm-12 expenses-sidebar-step3">
                {/* <DatePicker
                    name="executionDate"
                    value={values.executionDate}
                    onChange={(date) => setFieldValue('executionDate', date)}
                    label={t('Expenses.Date')}
                  /> */}
                <MinthrDateRangePicker
                  label={t('Expenses.Date')}
                  startDateName="executionDate"
                  singleDatePicker
                  onChange={(date) => setFieldValue('executionDate', date)}
                  saveFormat="YYYY-MM-DD"
                />
              </div>
            </div>

            <div className="row g-0 minthr-mb-5">
              <div className="col-sm-12 expenses-sidebar-step4">
                <Input
                  type="text"
                  name="description"
                  label={t('Expenses.comment')}
                  className="minthr-full-input"
                />
              </div>
            </div>
            {projectsList && projectsList.length > 0 && (
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12 expenses-sidebar-step2">
                  <MinthrSelect
                    key={`project-${values.affectedUserId}-${projectsList.length}`} // Add key to force re-render
                    options={projectsList.map((item) => ({
                      label: item.text,
                      value: item.value,
                    }))}
                    name="projectId"
                    value={values.projectId}
                    label={t('ProjectName')}
                    onchange={(e: SelectOptionResponse) => {
                      setFieldValue('projectId', e.value);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="minthr-mt-5 minthr-mb-5 minthr-flex-justify-center minthr-flex add-files">
              <button
                onClick={toggleFileVisibility}
                type="button"
                className="minthr-btn minthr-btn-success minthr-mobile-ml-auto minthr-mobile-px-1 w-100 opened expenses-sidebar-step5"
              >
                {isFileVisible ? t('btn.close') : t('btn.filearea.show')}
              </button>
            </div>

            <div className="row g-0 minthr-mb-5 file-area">
              <div className="col-sm-12">
                <FileUpload
                  name="files"
                  allowedFileTypes={[
                    'png',
                    'jpg',
                    'jpeg',
                    'application/pdf',
                    'docx',
                    'doc',
                    'xls',
                    'xlsx',
                    'ppt',
                    'pptx',
                    'pdf',
                  ]}
                  initialFiles={values.files || []}
                  existingFiles={filesNames}
                  onCustomDelete={handelDeleteFile}
                  isUploadVisible={isFileVisible}
                />
              </div>
            </div>

            <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-items-end minthr-py-4 minthr-mx-n5 minthr-px-6 minthr-mt-auto">
              <button
                type="button"
                className="minthr-btn minthr-btn-outline minthr-btn-primary minthr-ml-auto minthr-sidebar-close"
                onClick={() => closeSidebar(CreateExpenseForm)}
              >
                {t('cancel')}
              </button>
              <Button
                type="submit"
                className="minthr-btn minthr-btn-primary minthr-ml-6 detail-depense-sidebar-open expenses-sidebar-step6"
                loading={isSubmitting}
              >
                {t('submit')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default CreateExpenseForm;
